import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ImageGallery = ({ images }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handlePrevious = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? images?.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedImageIndex((prev) => (prev === images?.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'ArrowLeft') handlePrevious();
    if (e?.key === 'ArrowRight') handleNext();
    if (e?.key === 'Escape') setIsFullscreen(false);
  };

  return (
    <div className="w-full">
      <div className="relative bg-muted rounded-lg overflow-hidden mb-4">
        <div className="aspect-[16/10] md:aspect-[16/9] lg:aspect-[16/8]">
          <Image
            src={images?.[selectedImageIndex]?.url}
            alt={images?.[selectedImageIndex]?.alt}
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => setIsFullscreen(true)}
          />
        </div>

        <button
          onClick={handlePrevious}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-card/90 hover:bg-card rounded-full flex items-center justify-center transition-smooth shadow-elevation-2"
          aria-label="Previous image"
        >
          <Icon name="ChevronLeft" size={24} strokeWidth={2} />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 bg-card/90 hover:bg-card rounded-full flex items-center justify-center transition-smooth shadow-elevation-2"
          aria-label="Next image"
        >
          <Icon name="ChevronRight" size={24} strokeWidth={2} />
        </button>

        <div className="absolute bottom-2 md:bottom-4 right-2 md:right-4 bg-secondary/80 text-secondary-foreground px-3 py-1 rounded-md font-caption text-sm">
          {selectedImageIndex + 1} / {images?.length}
        </div>

        <button
          onClick={() => setIsFullscreen(true)}
          className="absolute top-2 md:top-4 right-2 md:right-4 w-10 h-10 bg-card/90 hover:bg-card rounded-full flex items-center justify-center transition-smooth shadow-elevation-2"
          aria-label="View fullscreen"
        >
          <Icon name="Maximize2" size={20} strokeWidth={2} />
        </button>
      </div>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 md:gap-3">
        {images?.map((image, index) => (
          <button
            key={index}
            onClick={() => handleThumbnailClick(index)}
            className={`relative aspect-square rounded-md overflow-hidden transition-smooth ${
              index === selectedImageIndex
                ? 'ring-2 ring-primary' :'ring-1 ring-border hover:ring-2 hover:ring-muted-foreground'
            }`}
            aria-label={`View image ${index + 1}`}
          >
            <Image
              src={image?.url}
              alt={image?.alt}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-background z-50 flex items-center justify-center"
          onClick={() => setIsFullscreen(false)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-card rounded-full flex items-center justify-center transition-smooth hover:bg-muted z-10"
            aria-label="Close fullscreen"
          >
            <Icon name="X" size={24} strokeWidth={2} />
          </button>

          <div className="relative w-full h-full flex items-center justify-center p-4">
            <Image
              src={images?.[selectedImageIndex]?.url}
              alt={images?.[selectedImageIndex]?.alt}
              className="max-w-full max-h-full object-contain"
            />

            <button
              onClick={(e) => {
                e?.stopPropagation();
                handlePrevious();
              }}
              className="absolute left-4 w-12 h-12 bg-card rounded-full flex items-center justify-center transition-smooth hover:bg-muted"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeft" size={24} strokeWidth={2} />
            </button>

            <button
              onClick={(e) => {
                e?.stopPropagation();
                handleNext();
              }}
              className="absolute right-4 w-12 h-12 bg-card rounded-full flex items-center justify-center transition-smooth hover:bg-muted"
              aria-label="Next image"
            >
              <Icon name="ChevronRight" size={24} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;